import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Link2, Search, CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import accountsAgentService from '../services/AccountsAgentService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import { buildTargets, savedLinkMap, suggestionsFor, searchTargets, toSaveLink, exactMatchOf, isExact } from '../utils/qboCustomerLinks';
import './OhipHotelLinkWizard.css';

// Admin > Billing > QuickBooks Customers > Link customers.
//
// Walks the QuickBooks customers that are NOT yet linked to an hconnect partner, chain or hotel and connects them:
//   1. Match   — each unlinked customer with up to three name-match suggestions (shared matching,
//                utils/qboCustomerLinks) and a search for anything else. EXACT name matches can be accepted in
//                one go; containment matches are shown but never pre-selected — "Rotana" inside "Rotana Hotels"
//                is likely, inside "Rotana Centro Capital" much less so.
//   2. Review  — the chosen pairs, before anything is written.
//   3. Done.
// Only links are written (acct_customer_links); QuickBooks itself is never changed. A full page with the step in
// the URL (STYLE.md), styled with the OHIP Link hotels wizard's stylesheet so the two wizards are one design.

const STEPS = [
    { key: 'match', label: 'Match customers' },
    { key: 'review', label: 'Review' },
    { key: 'done', label: 'Done' },
];
const STEP_KEYS = STEPS.map(s => s.key);

function QuickBooksCustomerLinkWizard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const stepParam = searchParams.get('step');
    const step = STEP_KEYS.includes(stepParam) ? stepParam : 'match';
    const goStep = useCallback((key) => {
        const next = new URLSearchParams(searchParams);
        next.set('step', key);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const [customers, setCustomers] = useState([]);
    const [hub, setHub] = useState({ links: [], accounts: [], hotels: [] });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const [choices, setChoices] = useState(() => new Map());     // customerId -> target key
    const [includeInactive, setIncludeInactive] = useState(false);
    const [show, setShow] = useState('all');
    const [search, setSearch] = useState('');
    const [picking, setPicking] = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [c, h] = await Promise.all([accountsAgentService.qboAllCustomers(), accountsAgentService.customerLinks()]);
            setCustomers(c);
            setHub(h);
        } catch (e) {
            setError(e.response?.data?.message || e.response?.data?.error?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const targets = useMemo(() => buildTargets(hub), [hub]);
    const targetByKey = useMemo(() => new Map(targets.map(t => [t.key, t])), [targets]);
    const saved = useMemo(() => savedLinkMap(hub), [hub]);

    // The work list: customers with no saved link, each with its suggestions worked out once.
    const unlinked = useMemo(() => customers
        .filter(c => !saved.get(String(c.customerId)))
        .filter(c => includeInactive || c.customerStatus === 'Active')
        .map(c => ({ ...c, suggestions: suggestionsFor(c, targets) })), [customers, saved, targets, includeInactive]);

    const exactPending = unlinked.filter(c => !choices.has(c.customerId) && exactMatchOf(c.suggestions)).length;

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return unlinked.filter(c => {
            if (show === 'suggested' && !c.suggestions.length) return false;
            if (show === 'nomatch' && c.suggestions.length) return false;
            if (show === 'chosen' && !choices.has(c.customerId)) return false;
            if (show === 'open' && choices.has(c.customerId)) return false;
            if (!needle) return true;
            return [c.customer, c.company, c.email, c.address].some(v => String(v || '').toLowerCase().includes(needle));
        });
    }, [unlinked, show, search, choices]);

    const choose = (id, key) => {
        setChoices(prev => { const n = new Map(prev); if (key) n.set(id, key); else n.delete(id); return n; });
        setPicking(null);
        setPickerSearch('');
    };

    // Only where a customer has ONE best exact match (exactMatchOf): two equally good matches is a decision.
    const acceptExact = () => setChoices(prev => {
        const n = new Map(prev);
        for (const c of unlinked) {
            const exact = exactMatchOf(c.suggestions);
            if (!n.has(c.customerId) && exact) n.set(c.customerId, exact.key);
        }
        return n;
    });

    const pickerResults = useMemo(() => searchTargets(targets, pickerSearch), [targets, pickerSearch]);
    const chosen = unlinked.filter(c => choices.has(c.customerId));

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const links = chosen.map(c => toSaveLink(String(c.customerId), choices.get(c.customerId)));
            const r = await accountsAgentService.saveCustomerLinks(links);
            setResult({ linked: r.linked, left: unlinked.length - chosen.length });
            setChoices(new Map());
            await load();
            goStep('done');
        } catch (e) {
            setError(e.response?.data?.message || e.response?.data?.error?.message || e.message);
        } finally {
            setBusy(false);
        }
    };

    const back = () => navigate('/admin/billing/quickbooks-customers');

    return (
        <div className="olw-page">
            <Button variant="secondary" onClick={back}><ArrowLeft className="icon" /> Back</Button>

            <div className="olw-header">
                <h1>Link QuickBooks customers</h1>
                <p className="olw-subtitle">
                    Find QuickBooks customers that are not connected to an hconnect partner, chain or hotel, and connect each to exactly one.
                    QuickBooks itself is not changed.
                </p>
                <div className="olw-steps">
                    {STEPS.map((s, i) => (
                        <span key={s.key} className={`olw-step ${s.key === step ? 'olw-step-active' : ''}`}>
                            <span className="olw-step-num">{i + 1}</span> {s.label}
                            {i < STEPS.length - 1 && <ChevronRight size={14} className="olw-step-sep" />}
                        </span>
                    ))}
                </div>
            </div>

            {error && <div className="olw-error"><AlertCircle size={16} /> {error}</div>}
            {loading && <div className="olw-loading"><Spinner /></div>}

            {!loading && step === 'match' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{unlinked.length}</strong> customers not linked</span>
                        <span><strong>{unlinked.filter(c => c.suggestions.length).length}</strong> with a suggestion</span>
                        <span><strong>{chosen.length}</strong> chosen</span>
                    </div>
                    <div className="olw-controls">
                        <label className="olw-grow">
                            <Search size={14} />
                            <input placeholder="Search customer, company, email or address" value={search} onChange={e => setSearch(e.target.value)} />
                        </label>
                        <select value={show} onChange={e => setShow(e.target.value)}>
                            <option value="all">All not linked</option>
                            <option value="suggested">With a suggestion</option>
                            <option value="nomatch">No suggestion</option>
                            <option value="open">Not chosen yet</option>
                            <option value="chosen">Chosen</option>
                        </select>
                        <label className="olw-check">
                            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive customers
                        </label>
                        <Button variant="secondary" onClick={acceptExact} disabled={!exactPending}>
                            <Link2 className="icon" /> Accept {exactPending} exact match(es)
                        </Button>
                    </div>

                    <table className="olw-table">
                        <thead><tr><th>QuickBooks customer</th><th>Balance</th><th>Connect to</th></tr></thead>
                        <tbody>
                            {visible.map(c => {
                                const choice = choices.get(c.customerId);
                                const t = choice ? targetByKey.get(choice) : null;
                                return (
                                    <tr key={c.customerId} className={choice ? 'olw-row-changed' : ''}>
                                        <td>
                                            {c.customer}
                                            {c.company && c.company !== c.customer && <div className="olw-muted">{c.company}</div>}
                                            {c.email && <div className="olw-muted">{c.email}</div>}
                                            {c.customerStatus !== 'Active' && <span className="olw-chip olw-chip-warn">{c.customerStatus}</span>}
                                        </td>
                                        <td>{c.balance ? Number(c.balance).toLocaleString('en-IE', { minimumFractionDigits: 2 }) : '—'} {c.currency}</td>
                                        <td>
                                            {t && (
                                                <div className="olw-linked">
                                                    <span>{t.label}</span>
                                                    <button type="button" className="olw-link-btn" onClick={() => choose(c.customerId, '')}>Clear</button>
                                                </div>
                                            )}
                                            {/* Guesses, not links — picking one chooses that one alone. */}
                                            {!t && c.suggestions.length > 0 && (
                                                <div className="olw-suggestions">
                                                    <span className="olw-suggest-heading">Suggested — pick one:</span>
                                                    {c.suggestions.map(s => (
                                                        <button key={s.key} type="button" className="olw-suggest" onClick={() => choose(c.customerId, s.key)}>
                                                            <Link2 size={13} /> {s.label}{isExact(s) ? (s.score > 3 ? ' · email' : ' · exact') : ''}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {picking === c.customerId ? (
                                                <div className="olw-picker">
                                                    <input autoFocus placeholder="Search partners, chains and hotels" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} />
                                                    {pickerResults.map(r => (
                                                        <button key={r.key} type="button" className="olw-pick" onClick={() => choose(c.customerId, r.key)}>
                                                            {r.label}{r.code ? ` (${r.code})` : ''}
                                                        </button>
                                                    ))}
                                                    {pickerSearch.trim() && !pickerResults.length && <div className="olw-muted">No partner, chain or hotel matches.</div>}
                                                    <button type="button" className="olw-link-btn" onClick={() => { setPicking(null); setPickerSearch(''); }}>Cancel</button>
                                                </div>
                                            ) : (
                                                <button type="button" className="olw-link-btn" onClick={() => { setPicking(c.customerId); setPickerSearch(''); }}>
                                                    {t ? 'Change' : 'Choose…'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!visible.length && (
                                <tr><td colSpan={3} className="olw-empty">
                                    {unlinked.length ? 'Nothing to show for this filter.' : 'Every customer is linked.'}
                                </td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => setChoices(new Map())} disabled={!chosen.length}>Clear choices</Button>
                        <Button variant="success" onClick={() => goStep('review')} disabled={!chosen.length}>
                            Review {chosen.length} link(s)
                        </Button>
                    </div>
                </div>
            )}

            {!loading && step === 'review' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{chosen.length}</strong> link(s) to save</span>
                        <span><strong>{unlinked.length - chosen.length}</strong> customer(s) left unlinked</span>
                    </div>
                    <table className="olw-table">
                        <thead><tr><th>QuickBooks customer</th><th>hconnect partner / chain / hotel</th></tr></thead>
                        <tbody>
                            {chosen.map(c => (
                                <tr key={c.customerId}>
                                    <td>{c.customer}{c.company && c.company !== c.customer && <div className="olw-muted">{c.company}</div>}</td>
                                    <td>{targetByKey.get(choices.get(c.customerId))?.label || choices.get(c.customerId)}</td>
                                </tr>
                            ))}
                            {!chosen.length && <tr><td colSpan={2} className="olw-empty">Nothing chosen — go back and match some customers.</td></tr>}
                        </tbody>
                    </table>
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => goStep('match')} disabled={busy}><ArrowLeft className="icon" /> Match customers</Button>
                        <Button variant="success" onClick={save} disabled={busy || !chosen.length}>Save {chosen.length} link(s)</Button>
                    </div>
                </div>
            )}

            {!loading && step === 'done' && (
                <div className="olw-card olw-done">
                    <CheckCircle2 size={28} className="olw-done-icon" />
                    <h2>Customers linked</h2>
                    {result
                        ? <ul><li>{result.linked} customer(s) linked</li><li>{result.left} still not linked</li></ul>
                        : <p className="olw-muted">{unlinked.length} customer(s) are not linked.</p>}
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => goStep('match')}>Link more customers</Button>
                        <Button variant="success" onClick={back}>Back to QuickBooks Customers</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QuickBooksCustomerLinkWizard;
